import React, { useState, useCallback, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Upload, FolderUp, FileText, Check, X, AlertTriangle, 
  File, Loader2, ChevronDown, ChevronRight, Trash2, Eye,
  FileType, CheckCircle, XCircle, Info, Users, FolderTree, Plus, ShieldCheck
} from 'lucide-react';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  SUPPORTED_UPLOAD_FORMATS,
} from '../utils/supportedUploadFormats';
import KnowledgeBadgePicker from './KnowledgeBadgePicker';
import DocumentTypePicker from './DocumentTypePicker';
import KnowledgeKeywordGroupPicker from './KnowledgeKeywordGroupPicker';
import TeamHierarchyPicker from './TeamHierarchyPicker';
import ReviewScheduleFields, { EMPTY_REVIEW_SCHEDULE, reviewSchedulePayload } from './ReviewScheduleFields';

// Supported file types
const SUPPORTED_EXTENSIONS = SUPPORTED_UPLOAD_FORMATS;
const MAX_FILE_SIZE_MB = 20;

const HoverHint = ({ label, children }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={label} className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700">
          <Info size={12} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const classifyUploadFailure = (error, filename) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const structured = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null;
  const rawMessage = String(structured?.message || detail || error?.message || '').trim();
  const lower = rawMessage.toLowerCase();
  const failure = (code, title, reason, nextStep, retryable = true, supportReference = null) => ({
    filename,
    code,
    title,
    reason,
    next_step: nextStep,
    retryable,
    support_reference: supportReference,
    http_status: status || null,
  });

  if (structured?.message) {
    return failure(
      structured.code || 'UPLOAD_FAILED',
      (structured.code || 'File processing issue').replaceAll('_', ' ').toLowerCase().replace(/^./, value => value.toUpperCase()),
      structured.message,
      structured.suggestion || 'Review the file and upload settings, then try again.',
      structured.retryable !== false,
      structured.support_reference || null,
    );
  }
  if (error?.code === 'ECONNABORTED' || lower.includes('timeout')) {
    return failure('UPLOAD_TIMEOUT', 'Upload timed out', 'Qflow did not receive a response before the upload timed out.', 'Check your connection and retry. If it repeats, upload fewer or smaller files at a time.');
  }
  if (!error?.response || error?.code === 'ERR_NETWORK' || lower.includes('network error')) {
    return failure('NETWORK_INTERRUPTED', 'Network connection interrupted', 'The connection ended before Qflow could finish the upload.', 'Check your internet connection, then retry the failed file. Qflow has kept it selected.');
  }
  if (status === 401) {
    return failure('SESSION_EXPIRED', 'Sign-in session expired', 'Your secure Qflow session expired during the upload.', 'Sign in again and retry the file.', false);
  }
  if (status === 403) {
    return failure('PERMISSION_DENIED', 'Upload permission required', rawMessage || 'You do not have permission to upload to the selected team or folder.', 'Choose a folder you can edit, or ask your tenant administrator to grant upload access.', false);
  }
  if (status === 413 || lower.includes('too large') || lower.includes('max is')) {
    return failure('FILE_TOO_LARGE', 'File is too large', rawMessage || 'The file exceeds Qflow’s current upload limit.', `Reduce or split the file so each file is no more than ${MAX_FILE_SIZE_MB} MB, then upload again.`, false);
  }
  if (lower.includes('unsupported') || lower.includes('file type')) {
    return failure('UNSUPPORTED_FILE_TYPE', 'Unsupported file type', rawMessage || 'Qflow does not support this file format.', 'Export the document as PDF, DOCX, PPTX, XLSX, TXT or a supported image format, then try again.', false);
  }
  if (status === 404 && (lower.includes('session') || lower.includes('expired'))) {
    return failure('UPLOAD_SESSION_EXPIRED', 'Upload session expired', 'The temporary upload session expired before processing completed.', 'Retry the failed file. If it happens again, keep the upload window open until it finishes.');
  }
  if (lower.includes('missing') && lower.includes('chunks')) {
    return failure('UPLOAD_INCOMPLETE', 'Upload was interrupted', 'Some parts of the file did not reach Qflow.', 'Check your connection and retry the failed file.');
  }
  if (lower.includes('extract') || lower.includes('readable text') || lower.includes('scanned') || lower.includes('ocr')) {
    return failure('TEXT_EXTRACTION_FAILED', 'Qflow could not read the document', rawMessage || 'Qflow could not find enough readable text in this file.', 'Open the file to confirm it is readable. For a scan or image-only PDF, upload a clearer scan or searchable PDF.', false);
  }
  if (lower.includes('password') || lower.includes('encrypted')) {
    return failure('PASSWORD_PROTECTED', 'Protected file', rawMessage || 'Qflow could not open this protected file.', 'Remove the password or encryption, save a new copy, and upload that copy.', false);
  }
  if (status >= 500) {
    return failure('SERVICE_UNAVAILABLE', 'Qflow service temporarily unavailable', 'Qflow could not process the file because a service was temporarily unavailable.', 'Wait a moment and retry once. If it repeats, check Flow Health or contact your Qflow administrator.');
  }
  return failure('UPLOAD_FAILED', 'File could not be processed', rawMessage || 'Qflow could not complete this upload.', 'Confirm the file opens normally and review the destination and access settings, then retry once.');
};

const BatchIngestion = ({
  user,
  teams = [],
  categories = [],
  onCategoriesChange,
  onIngestionComplete,
  triggerLabel = 'Upload Files',
  triggerClassName = 'bg-gray-900 hover:bg-gray-800 text-white',
  triggerVariant,
  triggerSize = 'sm',
  triggerTestId = 'upload-files-btn',
  initialTeamId = '',
  requireTeam = false,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dialogOpen = controlledOpen === undefined ? isDialogOpen : controlledOpen;
  const setDialogOpen = useCallback((nextOpen) => {
    if (controlledOpen === undefined) setIsDialogOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [controlledOpen, onOpenChange]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [expandedPreview, setExpandedPreview] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [customizingFile, setCustomizingFile] = useState('');
  const [fileMetadataOverrides, setFileMetadataOverrides] = useState({});
  const [isIngesting, setIsIngesting] = useState(false);
  const [step, setStep] = useState('select'); // select, preview, ingesting, complete
  
  // Configuration
  const [sourceType, setSourceType] = useState('');
  const [sourceTypeName, setSourceTypeName] = useState('');
  const [teamId, setTeamId] = useState(initialTeamId || '');
  const [categoryId, setCategoryId] = useState('');
  const [localCategories, setLocalCategories] = useState(categories);
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [allowedRoles, setAllowedRoles] = useState('');
  const [allowedTeams, setAllowedTeams] = useState('');
  const [badgeIds, setBadgeIds] = useState([]);
  const [sourcePublishedAt, setSourcePublishedAt] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [keywordGroupIds, setKeywordGroupIds] = useState([]);
  const [effectiveUntil, setEffectiveUntil] = useState('');
  const [lastReviewedAt, setLastReviewedAt] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [reviewSchedule, setReviewSchedule] = useState(EMPTY_REVIEW_SCHEDULE);
  useEffect(() => {
    if (!dialogOpen) return;
    setTeamId(initialTeamId || '');
  }, [dialogOpen, initialTeamId]);

  useEffect(() => setLocalCategories(categories || []), [categories]);
  
  // Results
  const [ingestionResult, setIngestionResult] = useState(null);
  // Live upload progress
  const [uploadProgress, setUploadProgress] = useState({
    currentFile: '',
    fileIndex: 0,
    totalFiles: 0,
    chunkIndex: 0,
    totalChunks: 0,
    chunkPercent: 0,
    phase: '', // 'uploading' | 'extracting' | 'indexing' | 'saving' | 'done'
  });
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const indexingWatchKey = (ingestionResult?.inserted_sources || [])
    .map(source => `${source.id}:${source.indexing_status || 'unknown'}`)
    .join('|');

  useEffect(() => {
    if (step !== 'complete' || !ingestionResult?.inserted_sources?.length) return;

    const hasActiveIndexing = ingestionResult.inserted_sources.some(source =>
      ['indexing', 'in_progress', 'queued'].includes(source.indexing_status)
    );
    if (!hasActiveIndexing) return;

    let cancelled = false;

    const pollIndexingStatus = async () => {
      const updated = await Promise.all(
        ingestionResult.inserted_sources.map(async (source) => {
          if (!source.id || !['indexing', 'in_progress', 'queued'].includes(source.indexing_status)) {
            return source;
          }

          try {
            const res = await axiosInstance.get(`/resolutions/${source.id}/indexing-status`);
            return {
              ...source,
              indexing_status: res.data.indexing_status || source.indexing_status,
              indexing_error: res.data.indexing_error || null,
              chunk_count: res.data.chunk_count ?? source.chunk_count,
            };
          } catch (error) {
            console.warn(`[BatchIngestion] Could not refresh indexing status for ${source.title}:`, error);
            return source;
          }
        })
      );

      if (!cancelled) {
        setIngestionResult(prev => prev ? { ...prev, inserted_sources: updated } : prev);
      }
    };

    pollIndexingStatus();
    const interval = setInterval(pollIndexingStatus, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, indexingWatchKey]);

  const getIndexingBadge = (status) => {
    const normalized = status || 'indexing';
    if (normalized === 'ready') {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    if (normalized === 'failed') {
      return 'bg-red-100 text-red-700 border-red-200';
    }
    if (normalized === 'not_indexed') {
      return 'bg-gray-100 text-gray-600 border-gray-200';
    }
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  const getIndexingLabel = (source) => {
    const status = source?.indexing_status || 'indexing';
    if (status === 'ready') return `Ready${source.chunk_count ? ` - ${source.chunk_count} chunks` : ''}`;
    if (status === 'failed') return 'Indexing failed';
    if (status === 'not_indexed') return 'Not indexed';
    return 'Indexing';
  };

  // Reset dialog state
  const resetDialog = () => {
    setSelectedFiles([]);
    setPreviews([]);
    setStep('select');
    setSourceType('');
    setSourceTypeName('');
    setTeamId(initialTeamId || '');
    setCategoryId('');
    setTeamPickerOpen(false);
    setCategoryPickerOpen(false);
    setNewCategoryName('');
    setNewCategoryParentId('');
    setDatesOpen(false);
    setVisibility('public');
    setAllowedRoles('');
    setAllowedTeams('');
    setBadgeIds([]);
    setSourcePublishedAt('');
    setEffectiveFrom('');
    setKeywordGroupIds([]);
    setCustomizingFile('');
    setFileMetadataOverrides({});
    setEffectiveUntil('');
    setLastReviewedAt('');
    setVersionLabel('');
    setReviewSchedule(EMPTY_REVIEW_SCHEDULE);
    setIngestionResult(null);
    setIsValidating(false);
    setIsIngesting(false);
    setUploadProgress({ currentFile: '', fileIndex: 0, totalFiles: 0, chunkIndex: 0, totalChunks: 0, chunkPercent: 0, phase: '' });
  };

  const categoryLabel = (category) => {
    const names = [category.name];
    const seen = new Set([category.id]);
    let parent = localCategories.find(item => item.id === category.parent_id);
    while (parent && !seen.has(parent.id)) {
      names.unshift(parent.name);
      seen.add(parent.id);
      parent = localCategories.find(item => item.id === parent.parent_id);
    }
    return names.join(' / ');
  };

  const splitAccessValues = (value) => value.split(',').map(item => item.trim()).filter(Boolean);

  const handleCategorySelection = (nextCategoryId, categoryOverride = null) => {
    setCategoryId(nextCategoryId);
    const category = categoryOverride || localCategories.find(item => item.id === nextCategoryId);
    if (!category?.settings_configured) return;
    setTeamId(category.default_team_id || '');
    setVisibility(category.default_visibility || 'public');
    setAllowedRoles((category.default_allowed_roles || []).join(', '));
    setAllowedTeams((category.default_allowed_teams || []).join(', '));
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.error('Enter a category name');
      return;
    }
    setCreatingCategory(true);
    try {
      const response = await axiosInstance.post('/categories/general', {
        name,
        icon: 'folder',
        description: '',
        parent_id: newCategoryParentId || null,
        settings_configured: true,
        inherit_parent_settings: Boolean(newCategoryParentId),
        default_team_id: teamId || null,
        default_visibility: visibility,
        default_allowed_roles: visibility === 'restricted' ? splitAccessValues(allowedRoles) : [],
        default_allowed_teams: visibility === 'restricted' ? splitAccessValues(allowedTeams) : [],
      });
      setLocalCategories(previous => [...previous, response.data]);
      handleCategorySelection(response.data.id, response.data);
      setNewCategoryName('');
      setNewCategoryParentId('');
      onCategoriesChange?.();
      toast.success(`Category “${response.data.name}” created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Handle file selection
  // Size limit (same as backend MAX_FILE_SIZE_MB)
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / 1_048_576).toFixed(1);
      toast.error(`${file.name}: exceeds maximum size (${sizeMB}MB > ${MAX_FILE_SIZE_MB}MB)`);
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(validateFileSize);
    if (files.length === 0) return;
    
    // Add files to selection
    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      extension: '.' + file.name.split('.').pop().toLowerCase(),
      selected: true,
      customTitle: '',
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  // Handle folder selection
  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(validateFileSize);
    if (files.length === 0) return;
    
    // Filter to supported files only
    const newFiles = files
      .filter(file => {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        return SUPPORTED_EXTENSIONS[ext];
      })
      .map(file => ({
        file,
        name: file.name,
        size: file.size,
        extension: '.' + file.name.split('.').pop().toLowerCase(),
        selected: true,
        customTitle: '',
        relativePath: file.webkitRelativePath || file.name,
      }));
    
    const unsupportedCount = files.length - newFiles.length;
    
    if (unsupportedCount > 0) {
      toast.warning(`${unsupportedCount} unsupported file(s) were excluded`);
    }
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Toggle file selection
  const toggleFileSelection = (index) => {
    setSelectedFiles(prev => prev.map((f, i) => 
      i === index ? { ...f, selected: !f.selected } : f
    ));
  };

  // Update custom title
  const updateCustomTitle = (index, title) => {
    setSelectedFiles(prev => prev.map((f, i) => 
      i === index ? { ...f, customTitle: title } : f
    ));
  };

  // Validate and preview files.
  //
  // Production reality: posting files (even in 5-file batches) through a single
  // multipart request flows through Cloudflare / ingress proxies that have hard
  // body-size + worker-timeout limits. A modest 30 MB PDF will return
  // "the origin web server sent a response that Cloudflare could not parse"
  // — exactly the demo-blocking error we saw.
  //
  // Solution:
  //   1. Validate everything we can client-side (extension, size, basic shape).
  //   2. Only call the server preview for SMALL files (<= 4 MB each) in batches
  //      whose combined size is also small (<= 8 MB), purely to render the
  //      first-500-chars text snippet.
  //   3. Large files just show their metadata + a "Preview text not available
  //      for large files" note, and proceed straight to the chunked upload
  //      path (which IS proxy-safe).
  const handlePreview = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files first');
      return;
    }
    if (!sourceType) {
      toast.error('Choose or add a document type');
      return;
    }
    if (requireTeam && !teamId) {
      toast.error('Choose a folder where you have edit access');
      return;
    }

    setIsValidating(true);

    try {
      const PER_FILE_PREVIEW_LIMIT_MB = 4;
      const BATCH_TOTAL_LIMIT_MB = 8;

      const clientPreviews = selectedFiles.map((f) => {
        const baseName = f.name.split('/').pop().split('\\').pop();
        const ext = (baseName.split('.').pop() || '').toLowerCase();
        const sizeMb = +(f.file.size / (1024 * 1024)).toFixed(2);
        const valid = Boolean(SUPPORTED_EXTENSIONS[`.${ext}`]);
        return {
          filename: baseName,
          size_mb: sizeMb,
          file_type: ext.toUpperCase(),
          extension: ext,
          status: valid ? 'pending' : 'unsupported',
          message: valid ? 'Ready to upload' : `Unsupported file type (.${ext})`,
          valid,
          suggested_title: baseName.replace(/\.[^.]+$/, ''),
          preview_text: null,
          page_count: null,
          _file: f.file,
        };
      });

      // Partition: small valid files → eligible for server preview;
      //            large valid files → skip server preview (still uploadable).
      const eligible = [];
      const skipped = [];
      for (const p of clientPreviews) {
        if (p.valid && p.size_mb <= PER_FILE_PREVIEW_LIMIT_MB) {
          eligible.push(p);
        } else {
          skipped.push(p);
        }
      }

      // Group eligible into batches whose TOTAL size <= BATCH_TOTAL_LIMIT_MB.
      const batches = [];
      let cur = [];
      let curMb = 0;
      for (const p of eligible) {
        if (curMb + p.size_mb > BATCH_TOTAL_LIMIT_MB && cur.length > 0) {
          batches.push(cur);
          cur = [];
          curMb = 0;
        }
        cur.push(p);
        curMb += p.size_mb;
      }
      if (cur.length) batches.push(cur);

      const byName = new Map(clientPreviews.map((p) => [p.filename, p]));

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const formData = new FormData();
        batch.forEach((p) => formData.append('files', p._file, p.filename));

        try {
          const previewParams = new URLSearchParams({ source_type: sourceType });
          if (teamId) previewParams.set('team_id', teamId);
          const response = await axiosInstance.post(`/batch-ingestion/preview?${previewParams.toString()}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          });
          for (const sp of response.data.previews || []) {
            const local = byName.get(sp.filename);
            if (local) {
              Object.assign(local, sp); // merge server-extracted preview_text/page_count/status
              delete local._file;
            }
          }
        } catch (err) {
          // Server preview failed (proxy / timeout / 5xx). Treat the files in
          // this batch as "preview unavailable" but still valid for upload.
          console.warn('[BatchIngestion] Server preview failed for batch', i + 1, err?.message);
          for (const p of batch) {
            p.preview_text = '(Preview unavailable — file will still upload normally)';
            delete p._file;
          }
        }
      }

      // Tag skipped files with an explanatory note
      for (const p of skipped) {
        if (p.valid) {
          p.preview_text = `(Preview skipped — file is ${p.size_mb} MB; upload will still proceed)`;
        }
        delete p._file;
      }

      const allPreviews = clientPreviews.map((p) => {
        const clean = { ...p };
        delete clean._file;
        return clean;
      });

      console.log(`[BatchIngestion] Preview complete: ${allPreviews.length} files`);
      setPreviews(allPreviews);
      setStep('preview');

      const invalidCount = allPreviews.filter((p) => !p.valid).length;
      if (invalidCount > 0) {
        toast.warning(`${invalidCount} file(s) have issues`);
      }
    } catch (error) {
      console.error('[BatchIngestion] Preview error:', error);
      const msg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Failed to preview files: ' + msg);
    } finally {
      setIsValidating(false);
    }
  };

  // Perform ingestion — chunked upload for ALL files (proxy-safe).
  //
  // Why no "fast path" for small files anymore:
  //   Posting whole files via multipart/form-data (even small ones) routes
  //   through Cloudflare / ingress, which has body-size + worker-timeout
  //   limits that produce the cryptic
  //     "the origin web server sent a response that Cloudflare could not parse"
  //   error in production. The chunked path is the only reliably-deliverable
  //   route under the production proxy, so we use it uniformly.
  const handleIngest = async () => {
    const filesToIngest = selectedFiles.filter(f => f.selected);

    if (filesToIngest.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setIsIngesting(true);
    setStep('ingesting');

    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB, well under any proxy body limit

    try {
      let totalSuccess = 0;
      let totalFailed = 0;
      let totalSkipped = 0;
      const allResults = [];
      const failedFiles = [];
      let lastBatchId = null;

      for (let fi = 0; fi < filesToIngest.length; fi++) {
        const f = filesToIngest[fi];
        const baseName = f.name.split('/').pop().split('\\').pop();
        const override = fileMetadataOverrides[f.name] || {};
        const fileSourceType = override.sourceType || sourceType;
        const fileSourceTypeName = override.sourceTypeName ?? sourceTypeName;
        const fileBadgeIds = override.badgeIds || badgeIds;
        const fileKeywordGroupIds = override.keywordGroupIds || keywordGroupIds;
        const totalChunks = Math.max(1, Math.ceil(f.size / CHUNK_SIZE));
        const fileSizeMB = (f.size / 1_048_576).toFixed(1);
        console.log(`[BatchIngestion] Chunked upload for ${baseName} (${fileSizeMB}MB, ${totalChunks} chunks)`);

        setUploadProgress({
          currentFile: baseName,
          fileIndex: fi + 1,
          totalFiles: filesToIngest.length,
          chunkIndex: 0,
          totalChunks,
          chunkPercent: 0,
          phase: 'uploading',
        });

        try {
          // 1. Init
          const initRes = await axiosInstance.post('/batch-ingestion/chunked/init', {
            filename: baseName,
            total_size: f.size,
            total_chunks: totalChunks,
            source_type: fileSourceType,
            team_id: teamId || null,
            category_id: categoryId || null,
          });
          const uploadId = initRes.data.upload_id;

          // 2. Upload chunks sequentially (most reliable on flaky networks)
          for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, f.size);
            const blob = f.file.slice(start, end);
            const fd = new FormData();
            fd.append('chunk', blob, `chunk_${i}`);
            await axiosInstance.post(
              `/batch-ingestion/chunked/${uploadId}/chunk?index=${i}`,
              fd,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
                onUploadProgress: (evt) => {
                  if (evt.total) {
                    const pct = Math.round((evt.loaded / evt.total) * 100);
                    setUploadProgress(prev => ({
                      ...prev,
                      chunkIndex: i + 1,
                      chunkPercent: pct,
                    }));
                  }
                },
              }
            );
            setUploadProgress(prev => ({ ...prev, chunkIndex: i + 1, chunkPercent: 100 }));
          }

          // 3. Complete (server extracts text, saves source, and starts indexing)
          setUploadProgress(prev => ({ ...prev, phase: 'extracting' }));
          const completionParams = new URLSearchParams({ source_type: fileSourceType });
          if (teamId && teamId !== '__none__') completionParams.set('team_id', teamId);
          if (categoryId) completionParams.set('category_id', categoryId);
          if (fileSourceTypeName.trim()) completionParams.set('source_type_name', fileSourceTypeName.trim());
          completionParams.set('visibility', visibility);
          if (visibility === 'restricted' && allowedRoles.trim()) completionParams.set('allowed_roles', allowedRoles.trim());
          if (visibility === 'restricted' && allowedTeams.trim()) completionParams.set('allowed_teams', allowedTeams.trim());
          if (fileBadgeIds.length) completionParams.set('badge_ids', fileBadgeIds.join(','));
          if (fileKeywordGroupIds.length) completionParams.set('keyword_group_ids', fileKeywordGroupIds.join(','));
          if (sourcePublishedAt) completionParams.set('source_published_at', sourcePublishedAt);
          if (effectiveFrom) completionParams.set('effective_from', effectiveFrom);
          if (effectiveUntil) completionParams.set('effective_until', effectiveUntil);
          if (lastReviewedAt) completionParams.set('last_reviewed_at', lastReviewedAt);
          if (versionLabel) completionParams.set('version_label', versionLabel);
          if (reviewSchedulePayload(reviewSchedule)) completionParams.set('review_schedule', JSON.stringify(reviewSchedulePayload(reviewSchedule)));
          const completeRes = await axiosInstance.post(
            `/batch-ingestion/chunked/${uploadId}/complete?${completionParams.toString()}`,
            {},
            { timeout: 120000 }
          );
          setUploadProgress(prev => ({ ...prev, phase: 'indexing' }));
          totalSuccess += 1;
          allResults.push({
            id: completeRes.data.resolution_id,
            title: completeRes.data.title,
            action: completeRes.data.action,
            indexing_status: completeRes.data.indexing || 'indexing',
            chunk_count: completeRes.data.chunk_count || 0,
          });
          setUploadProgress(prev => ({ ...prev, phase: 'saving', chunkPercent: 100 }));
        } catch (err) {
          totalFailed += 1;
          console.error(`[BatchIngestion] Chunked upload failed for ${baseName}:`, err);
          const failure = classifyUploadFailure(err, baseName);
          failedFiles.push(failure);
          toast.error(`${baseName}: ${failure.reason}`);
        }
      }

      setIngestionResult({
        successful: totalSuccess,
        failed: totalFailed,
        total_files: totalSuccess + totalFailed + totalSkipped,
        inserted_sources: allResults,
        failed_files: failedFiles,
        batch_id: lastBatchId,
        skipped: totalSkipped,
      });
      setUploadProgress(prev => ({ ...prev, phase: 'done', chunkPercent: 100 }));
      setStep('complete');

      if (totalSuccess > 0) {
        toast.success(
          `${totalSuccess} source(s) saved. Indexing is running in the background and may take up to a minute.`,
          { duration: 5000 }
        );
        if (onIngestionComplete) onIngestionComplete();
      }

      if (totalFailed > 0) {
        toast.warning(`${totalFailed} file(s) failed to process`);
      }
    } catch (error) {
      toast.error('Ingestion failed: ' + (error.response?.data?.detail || error.message));
      setStep('preview');
    } finally {
      setIsIngesting(false);
    }
  };

  const handleReviewFailedFiles = () => {
    const failedNames = new Set((ingestionResult?.failed_files || []).map(item => item.filename));
    setSelectedFiles(previous => previous.map(file => {
      const baseName = file.name.split('/').pop().split('\\').pop();
      return { ...file, selected: failedNames.has(baseName) };
    }));
    setIngestionResult(null);
    setUploadProgress({ currentFile: '', fileIndex: 0, totalFiles: 0, chunkIndex: 0, totalChunks: 0, chunkPercent: 0, phase: '' });
    setStep('preview');
    toast.info('Only the failed files are selected. Review the settings, then try again.');
  };

  // Get file type info
  const getFileTypeInfo = (extension) => {
    return SUPPORTED_EXTENSIONS[extension] || { name: 'Unknown', icon: '📄', color: 'text-gray-500' };
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Render file selection step
  const renderSelectStep = () => (
    <div className="space-y-4">
      {/* Upload Options */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-3">
        {/* Upload Files */}
        <button
          type="button"
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-900 px-3 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={15} /> Upload files
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORTED_UPLOAD_ACCEPT}
            className="hidden"
            onChange={handleFileSelect}
          />
        </button>

        {/* Upload Folder */}
        <button
          type="button"
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50"
          onClick={() => folderInputRef.current?.click()}
        >
          <FolderUp size={15} /> Upload folder
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory="true"
            directory="true"
            className="hidden"
            onChange={handleFolderSelect}
          />
        </button>
        <p className="ml-auto text-right text-[11px] leading-5 text-slate-500">
          PDF, Word, PowerPoint, Excel and text<br />Including scanned images · up to {MAX_FILE_SIZE_MB} MB each
        </p>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFiles([])}
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 size={14} className="mr-1" /> Clear All
            </Button>
          </div>
          
          <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
            {selectedFiles.map((file, index) => {
              const typeInfo = getFileTypeInfo(file.extension);
              const isSupported = !!SUPPORTED_EXTENSIONS[file.extension];
              
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 rounded ${
                    isSupported ? 'bg-white hover:bg-gray-50' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span>{typeInfo.icon}</span>
                    <span className="text-sm truncate">{file.name}</span>
                    <span className="text-xs text-gray-400">{formatSize(file.size)}</span>
                    {!isSupported && (
                      <span className="text-xs text-red-500 flex items-center">
                        <AlertTriangle size={12} className="mr-1" /> Unsupported
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Configuration */}
      {selectedFiles.length > 0 && (
        <div className="border-t pt-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Classification</p>
            <p className="text-xs text-slate-500">Describe this batch without forcing it into a fixed list.</p>
          </div>
          
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DocumentTypePicker
              value={sourceType}
              onChange={(nextValue, option) => {
                setSourceType(nextValue);
                setSourceTypeName(option?.name || '');
              }}
              compact
            />
            <KnowledgeBadgePicker value={badgeIds} onChange={setBadgeIds} user={user} compact inlineCreate />
          </div>
          <KnowledgeKeywordGroupPicker value={keywordGroupIds} onChange={setKeywordGroupIds} compact />
          <p className="text-[11px] text-slate-500">These choices apply to every selected file. You can customise an individual file on the next screen.</p>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <p className="text-sm font-semibold text-slate-900">Access and ownership</p>
              <HoverHint label="About access and ownership">Assign a registered team, then optionally place the document in a shared category.</HoverHint>
            </div>
            <div className="space-y-2 rounded-xl bg-slate-50 p-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700"><Users size={16} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">Team</p>
                  <p className="truncate text-xs text-slate-500">{teams.find(team => team.id === teamId)?.fullPath || teams.find(team => team.id === teamId)?.name || 'No team assigned'}</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setTeamPickerOpen(open => !open)} aria-expanded={teamPickerOpen}>Assign</Button>
              </div>
              {teamPickerOpen && (
                <div className="rounded-lg border border-slate-200 bg-white p-2" data-testid="batch-team-selector">
                  <TeamHierarchyPicker
                    teams={teams}
                    value={teamId}
                    onChange={(nextTeamId) => setTeamId(nextTeamId)}
                    allowUnassigned={!requireTeam}
                  />
                </div>
              )}

              <div className="border-t border-slate-200 pt-2">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700"><FolderTree size={16} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">Category <span className="font-normal text-slate-400">Optional</span></p>
                    <p className="truncate text-xs text-slate-500">{localCategories.find(category => category.id === categoryId) ? categoryLabel(localCategories.find(category => category.id === categoryId)) : 'No category assigned'}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setCategoryPickerOpen(open => !open)} aria-expanded={categoryPickerOpen}>Assign</Button>
                </div>
                {categoryPickerOpen && (
                  <div className="mt-2 grid gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Add to existing category</Label>
                      <select value={categoryId} onChange={(event) => handleCategorySelection(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="batch-category-selector">
                        <option value="">No category</option>
                        {[...localCategories].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b))).map(category => (
                          <option key={category.id} value={category.id}>{categoryLabel(category)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Add new category</Label>
                      <Input value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} placeholder="Category name" data-testid="batch-new-category-name" />
                      <select value={newCategoryParentId} onChange={(event) => setNewCategoryParentId(event.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-xs">
                        <option value="">Top level</option>
                        {[...localCategories].filter(category => !category.parent_id).sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b))).map(category => (
                          <option key={category.id} value={category.id}>Under {categoryLabel(category)}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500">Child folders inherit the parent folder’s team and access defaults.</p>
                      <Button type="button" variant="outline" size="sm" onClick={handleCreateCategory} disabled={creatingCategory || !newCategoryName.trim()} className="w-full">
                        {creatingCategory ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />} Create and assign
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><ShieldCheck size={15} className="text-sky-600" /> Access control</p>
              <p className="text-xs text-slate-500">Choose who can view these documents.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="grid gap-2 sm:grid-cols-2" data-testid="batch-visibility-toggle">
                {[
                  ['public', 'Public', 'Visible to everyone in this organisation'],
                  ['restricted', 'Restricted', 'Only approved roles and teams'],
                ].map(([value, label, description]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setVisibility(value)}
                    aria-pressed={visibility === value}
                    className={`flex min-h-[62px] items-start gap-2 rounded-lg border p-3 text-left transition-colors ${visibility === value ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-100' : 'border-slate-200 bg-white hover:border-sky-300'}`}
                    data-testid={`batch-visibility-${value}`}
                  >
                    <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${visibility === value ? 'border-[5px] border-sky-600' : 'border-slate-300'}`} />
                    <span><strong className="block text-sm font-medium text-slate-900">{label}</strong><span className="text-xs text-slate-500">{description}</span></span>
                  </button>
                ))}
              </div>
              {visibility === 'restricted' && (
                <div className="mt-3 grid gap-3 border-t border-slate-200 pt-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Allowed roles <span className="font-normal text-slate-400">Comma-separated</span></Label>
                    <Input value={allowedRoles} onChange={(event) => setAllowedRoles(event.target.value)} placeholder="e.g. admin, team_manager" className="mt-1 bg-white" data-testid="batch-allowed-roles" />
                  </div>
                  <div>
                    <Label className="text-xs">Allowed teams <span className="font-normal text-slate-400">Comma-separated team IDs</span></Label>
                    <Input value={allowedTeams} onChange={(event) => setAllowedTeams(event.target.value)} placeholder="e.g. team-legal, team-compliance" className="mt-1 bg-white" data-testid="batch-allowed-teams" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            <button type="button" onClick={() => setDatesOpen(open => !open)} aria-expanded={datesOpen} className="flex w-full items-center justify-between px-3 py-2 text-left">
              <span className="text-xs font-medium text-gray-700">Document dates and review <span className="font-normal text-slate-400">Optional · {reviewSchedulePayload(reviewSchedule) ? 'Review reminders on' : sourcePublishedAt || effectiveFrom || effectiveUntil || lastReviewedAt || versionLabel ? 'Configured' : 'Not set'}</span></span>
              {datesOpen ? <ChevronDown size={15} className="rotate-180 text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>
            {datesOpen && <div className="grid grid-cols-2 gap-3 border-t border-slate-200 bg-slate-50 p-3 md:grid-cols-3">
              <div>
                <Label className="text-xs">Published</Label>
                <Input type="date" value={sourcePublishedAt} onChange={event => setSourcePublishedAt(event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Effective from</Label>
                <Input type="date" value={effectiveFrom} onChange={event => setEffectiveFrom(event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Effective until</Label>
                <Input type="date" value={effectiveUntil} onChange={event => setEffectiveUntil(event.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs">Last reviewed</Label>
                <Input type="date" value={lastReviewedAt} onChange={event => setLastReviewedAt(event.target.value)} className="mt-1 bg-white" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Version label</Label>
                <Input value={versionLabel} onChange={event => setVersionLabel(event.target.value)} placeholder="e.g. 2026.2" className="mt-1 bg-white" />
              </div>
              <ReviewScheduleFields value={reviewSchedule} onChange={setReviewSchedule} anchorDate={lastReviewedAt || sourcePublishedAt || effectiveFrom} teamName={teams.find(team => team.id === teamId)?.name} />
            </div>}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => setDialogOpen(false)}>
          Cancel
        </Button>
        <Button
          onClick={handlePreview}
          disabled={selectedFiles.length === 0 || isValidating}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isValidating ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" /> Validating...
            </>
          ) : (
            <>
              <Eye size={16} className="mr-2" /> Preview Files
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Render preview step
  const renderPreviewStep = () => {
    const validCount = previews.filter(p => p.valid).length;
    const invalidCount = previews.length - validCount;
    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-500" size={18} />
            <span className="text-sm">{validCount} ready</span>
          </div>
          {invalidCount > 0 && (
            <div className="flex items-center gap-2">
              <XCircle className="text-red-500" size={18} />
              <span className="text-sm">{invalidCount} issues</span>
            </div>
          )}
        </div>

        {/* Configuration Summary */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>
            <strong>Type:</strong> {sourceTypeName.trim() || 'Not selected'}
          </span>
          {teamId && teams.find(t => t.id === teamId) && (
            <span>
              <strong>Team:</strong> {teams.find(t => t.id === teamId)?.name}
            </span>
          )}
          {categoryId && localCategories.find(category => category.id === categoryId) && (
            <span><strong>Category:</strong> {categoryLabel(localCategories.find(category => category.id === categoryId))}</span>
          )}
          <span><strong>Access:</strong> {visibility === 'restricted' ? 'Restricted' : 'Public'}</span>
        </div>

        {/* Files Table */}
        <div className="max-w-full overflow-hidden rounded-lg border">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 w-8">
                  <input
                    type="checkbox"
                    checked={selectedFiles.every(f => f.selected)}
                    onChange={(e) => {
                      setSelectedFiles(prev => prev.map(f => ({ ...f, selected: e.target.checked })));
                    }}
                    className="rounded"
                  />
                </th>
                <th className="text-left p-2">File</th>
                <th className="text-left p-2 w-24">Status</th>
                <th className="text-left p-2 w-16">Pages</th>
                <th className="text-left p-2 w-24">Size</th>
              </tr>
            </thead>
            <tbody>
              {selectedFiles.map((file, index) => {
                const preview = previews.find(p => p.filename === file.name);
                const isValid = preview?.valid !== false;
                
                return (
                  <tr key={index} className={`border-b last:border-0 ${!isValid ? 'bg-red-50' : ''}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={file.selected && isValid}
                        onChange={() => toggleFileSelection(index)}
                        disabled={!isValid}
                        className="rounded"
                      />
                    </td>
                    <td className="min-w-0 p-2">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0">{getFileTypeInfo(file.extension).icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium" title={file.name}>{file.name}</p>
                          {preview?.valid === false && preview?.message && (
                            <p className="mt-1 text-xs text-red-700" role="alert">{preview.message}</p>
                          )}
                          {preview?.preview_text && (
                            <div className="mt-1">
                              <button
                                type="button"
                                onClick={() => setExpandedPreview(expandedPreview === file.name ? null : file.name)}
                                className="flex items-center gap-1 text-left text-xs text-blue-600 hover:text-blue-800"
                              >
                                {expandedPreview === file.name ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                {expandedPreview === file.name ? 'Hide extracted text' : 'Review extracted text'}
                              </button>
                              {preview.extraction_method === 'ocr' && (
                                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  preview.ocr_quality === 'high' ? 'bg-green-100 text-green-700' :
                                  preview.ocr_quality === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  OCR {preview.ocr_quality || 'unknown'}{preview.ocr_confidence != null ? ` · ${preview.ocr_confidence}%` : ''}
                                </span>
                              )}
                              {expandedPreview === file.name && (
                                <pre className="mt-2 max-h-48 max-w-2xl overflow-auto whitespace-pre-wrap rounded-md border bg-gray-50 p-3 text-xs text-gray-700">
                                  {preview.preview_text}
                                </pre>
                              )}
                            </div>
                          )}
                          {isValid && (
                            <div className="mt-2">
                              <button
                                type="button"
                                className="text-xs font-medium text-slate-600 hover:text-blue-700"
                                onClick={() => {
                                  setCustomizingFile(current => current === file.name ? '' : file.name);
                                  setFileMetadataOverrides(current => current[file.name] ? current : {
                                    ...current,
                                    [file.name]: { sourceType, sourceTypeName, badgeIds: [...badgeIds], keywordGroupIds: [...keywordGroupIds] },
                                  });
                                }}
                              >
                                {customizingFile === file.name ? 'Hide customisation' : 'Customise this file'}
                              </button>
                              {customizingFile === file.name && (
                                <div className="mt-2 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                  <DocumentTypePicker value={fileMetadataOverrides[file.name]?.sourceType || sourceType} onChange={(nextValue, option) => setFileMetadataOverrides(current => ({ ...current, [file.name]: { ...(current[file.name] || {}), sourceType: nextValue, sourceTypeName: option?.name || '' } }))} compact />
                                  <KnowledgeBadgePicker value={fileMetadataOverrides[file.name]?.badgeIds || []} onChange={nextValue => setFileMetadataOverrides(current => ({ ...current, [file.name]: { ...(current[file.name] || {}), badgeIds: nextValue } }))} user={user} compact />
                                  <KnowledgeKeywordGroupPicker value={fileMetadataOverrides[file.name]?.keywordGroupIds || []} onChange={nextValue => setFileMetadataOverrides(current => ({ ...current, [file.name]: { ...(current[file.name] || {}), keywordGroupIds: nextValue } }))} compact />
                                  <Button type="button" size="sm" variant="ghost" onClick={() => {
                                    setFileMetadataOverrides(current => {
                                      const next = { ...current };
                                      delete next[file.name];
                                      return next;
                                    });
                                    setCustomizingFile('');
                                  }}>Reset to batch choices</Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-2">
                      {isValid ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                          <Check size={12} /> Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                          <AlertTriangle size={12} /> {preview?.status || 'Error'}
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-gray-500">
                      {preview?.page_count || '-'}
                    </td>
                    <td className="p-2 text-gray-500">
                      {formatSize(file.size)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <Button variant="outline" onClick={() => setStep('select')}>
            ← Back
          </Button>
          <Button
            onClick={handleIngest}
            disabled={selectedFiles.filter(f => f.selected).length === 0}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            <Upload size={16} className="mr-2" />
            Ingest {selectedFiles.filter(f => f.selected).length} Selected Files
          </Button>
        </div>
      </div>
    );
  };

  // Render ingesting step
  const renderIngestingStep = () => {
    const { currentFile, fileIndex, totalFiles, chunkIndex, totalChunks, chunkPercent, phase } = uploadProgress;

    // Overall percentage: fully-completed files count as 100%, plus partial progress of current file.
    const overallPct = totalFiles > 0
      ? Math.min(100, Math.round(((fileIndex - 1) / totalFiles) * 100 + (chunkPercent / totalFiles)))
      : 0;

    let subLabel = 'Preparing upload...';
    if (phase === 'uploading' && totalChunks > 0) {
      subLabel = `Uploading chunk ${chunkIndex} of ${totalChunks} · ${chunkPercent}%`;
    } else if (phase === 'extracting') {
      subLabel = 'Extracting text and reading document structure...';
    } else if (phase === 'indexing') {
      subLabel = 'Chunking and preparing the source for search...';
    } else if (phase === 'saving') {
      subLabel = 'Saving the source record...';
    } else if (phase === 'batching') {
      subLabel = `Uploading batch ${chunkIndex} of ${totalChunks} · ${chunkPercent}%`;
    }

    const stages = [
      { id: 'uploading', label: 'Upload' },
      { id: 'extracting', label: 'Extract text' },
      { id: 'indexing', label: 'Chunk and index' },
      { id: 'saving', label: 'Save source' },
    ];
    const stageIndex = Math.max(0, stages.findIndex(stage => stage.id === phase));

    return (
      <div className="py-10 px-2">
        <div className="flex items-center gap-3 mb-4">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-base font-medium text-gray-800 truncate" data-testid="upload-current-file">
              {currentFile || 'Processing...'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-testid="upload-sub-label">{subLabel}</p>
          </div>
          <span className="text-sm font-semibold text-blue-600 tabular-nums" data-testid="upload-overall-pct">
            {overallPct}%
          </span>
        </div>

        {/* Overall progress bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-200 ease-out"
            style={{ width: `${overallPct}%` }}
            data-testid="upload-overall-bar"
          />
        </div>

        {/* Per-file counter */}
        {totalFiles > 0 && (
          <p className="text-[11px] text-gray-400 text-center mt-3">
            File {Math.min(fileIndex, totalFiles)} of {totalFiles}
            {phase === 'uploading' && totalChunks > 1 && (
              <span> · chunk {chunkIndex}/{totalChunks}</span>
            )}
          </p>
        )}

        <div className="grid grid-cols-4 gap-2 mt-5" data-testid="upload-indexing-stages">
          {stages.map((stage, idx) => {
            const isDone = idx < stageIndex;
            const isCurrent = idx === stageIndex;
            return (
              <div
                key={stage.id}
                className={`rounded-md border px-2 py-2 text-center text-[11px] ${
                  isDone
                    ? 'border-green-200 bg-green-50 text-green-700'
                    : isCurrent
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-gray-50 text-gray-400'
                }`}
              >
                <div className="flex justify-center mb-1">
                  {isDone ? (
                    <CheckCircle size={14} />
                  ) : isCurrent ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <div className="w-3.5 h-3.5 rounded-full border border-gray-300" />
                  )}
                </div>
                {stage.label}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-400 text-center mt-4 italic">
          Keep this window open until upload completes. A source is searchable once its indexing badge says Ready.
        </p>
      </div>
    );
  };

  // Render complete step
  const renderCompleteStep = () => {
    const sources = ingestionResult?.inserted_sources || [];
    const failures = ingestionResult?.failed_files || [];
    const hasFailures = failures.length > 0;
    const allFailed = hasFailures && (ingestionResult?.successful || 0) === 0;
    const readyCount = sources.filter(source => (source.indexing_status || '').toLowerCase() === 'ready').length;
    const failedIndexingCount = sources.filter(source => (source.indexing_status || '').toLowerCase() === 'failed').length;
    const activeIndexingCount = Math.max(0, sources.length - readyCount - failedIndexingCount);
    const indexingPct = sources.length > 0 ? Math.round((readyCount / sources.length) * 100) : 100;

    return (
    <div className="space-y-4 py-8">
      <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${allFailed ? 'bg-red-100' : hasFailures ? 'bg-amber-100' : 'bg-green-100'}`}>
        {allFailed ? <XCircle className="h-8 w-8 text-red-600" /> : hasFailures ? <AlertTriangle className="h-8 w-8 text-amber-600" /> : <CheckCircle className="h-8 w-8 text-green-600" />}
      </div>
      
      <div className="text-center">
        <p className="text-lg font-medium text-gray-700">{allFailed ? 'Upload needs attention' : hasFailures ? 'Upload partly completed' : 'Upload complete'}</p>
        <p className="text-sm text-gray-500 mt-1">
          {ingestionResult?.successful} of {ingestionResult?.total_files} files processed successfully
        </p>
      </div>

      {hasFailures && (
        <div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-red-50 p-4 text-left" data-testid="upload-failure-guidance">
          <div className="mb-3">
            <p className="text-sm font-semibold text-red-900">Files needing attention</p>
            <p className="mt-0.5 text-xs text-red-700">Qflow kept the failed files so you can correct the issue and retry without starting again.</p>
          </div>
          <div className="max-h-64 space-y-3 overflow-y-auto">
            {failures.map((failure, index) => (
              <div key={`${failure.filename}-${index}`} className="rounded-lg border border-red-100 bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="min-w-0 break-all text-sm font-medium text-slate-900">{failure.filename}</p>
                  <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">{failure.title}</span>
                </div>
                <p className="mt-2 text-sm text-slate-700"><strong>Why it failed:</strong> {failure.reason}</p>
                <p className="mt-1 text-sm text-slate-700"><strong>What to do:</strong> {failure.next_step}</p>
                {failure.support_reference && (
                  <p className="mt-2 text-xs font-medium text-slate-500">Support reference: {failure.support_reference}</p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-600">If the same file fails again after following the advice, send its filename and any support reference shown to your Qflow administrator.</p>
        </div>
      )}

      {sources.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-left max-w-md mx-auto">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-blue-900">Indexing progress</p>
              <p className="text-xs text-blue-700 mt-0.5">
                {readyCount} of {sources.length} source(s) searchable
                {activeIndexingCount > 0 ? `, ${activeIndexingCount} still indexing` : ''}
                {failedIndexingCount > 0 ? `, ${failedIndexingCount} failed` : ''}
              </p>
            </div>
            <span className="text-sm font-semibold text-blue-700 tabular-nums" data-testid="indexing-progress-pct">
              {indexingPct}%
            </span>
          </div>
          <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden mt-3">
            <div
              className={`h-full transition-all duration-200 ${failedIndexingCount > 0 ? 'bg-yellow-500' : 'bg-blue-500'}`}
              style={{ width: `${indexingPct}%` }}
              data-testid="indexing-progress-bar"
            />
          </div>
          {activeIndexingCount > 0 && (
            <p className="text-xs text-blue-700 mt-2">
              You can close this dialog, but wait until sources show Ready before testing answers from newly uploaded files.
            </p>
          )}
        </div>
      )}

      {/* Results Summary */}
      {ingestionResult && (
        <div className="bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-500" size={14} />
              <span>Successful: {ingestionResult.successful}</span>
            </div>
            {ingestionResult.failed > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="text-red-500" size={14} />
                <span>Failed: {ingestionResult.failed}</span>
              </div>
            )}
            {ingestionResult.skipped > 0 && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={14} />
                <span>Skipped: {ingestionResult.skipped}</span>
              </div>
            )}
          </div>
          
          {ingestionResult.batch_id && (
            <p className="text-xs text-gray-400 mt-2">
              Batch ID: {ingestionResult.batch_id}
            </p>
          )}
        </div>
      )}

      {/* Created Sources */}
      {ingestionResult?.inserted_sources?.length > 0 && (
        <div className="border rounded-lg p-3 max-w-md mx-auto text-left">
          <p className="text-xs font-medium text-gray-500 mb-2">Created Sources:</p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {ingestionResult.inserted_sources.map((source, idx) => (
              <div key={source.id || idx} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className="block truncate font-medium text-gray-700">{source.title}</span>
                  {source.indexing_error && (
                    <span className="block text-xs text-red-600 truncate" title={source.indexing_error}>
                      {source.indexing_error}
                    </span>
                  )}
                </div>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${getIndexingBadge(source.indexing_status)}`}>
                  {getIndexingLabel(source)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2 pt-2">
        {hasFailures && (
          <Button type="button" variant="outline" onClick={handleReviewFailedFiles} data-testid="review-retry-failed-files">
            Review and retry failed files
          </Button>
        )}
        <Button onClick={() => { resetDialog(); setDialogOpen(false); }}>
          {hasFailures ? 'Close for now' : 'Done'}
        </Button>
      </div>
    </div>
    );
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={(nextOpen) => { setDialogOpen(nextOpen); if (!nextOpen) resetDialog(); }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            size={triggerSize}
            variant={triggerVariant}
            className={triggerClassName}
            data-testid={triggerTestId}
          >
            <Upload size={14} className="mr-1.5" /> {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-x-hidden overflow-y-auto p-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 px-6 pt-6">
            <Upload className="text-gray-700" size={20} />
            Upload knowledge
          </DialogTitle>
          <p className="px-6 text-sm text-gray-500">
            Add files, classify them, and choose who owns and can access the knowledge.
          </p>
        </DialogHeader>
        
        <div className="mt-4 border-t border-slate-200 px-6 py-4">
          {step === 'select' && renderSelectStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'ingesting' && renderIngestingStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchIngestion;
